// Publishing one note to a read-only link, and taking it back.
//
// The owner's half of the share feature. The page a stranger reads is
// `share/SharedNote.tsx`, which is not a screen in this shell at all.
//
// THE WORDING IS THE FEATURE, and it is why this is a block with room to write
// in rather than a toggle in the title bar. People read "unlisted" as "private"
// and act accordingly, so every state here says, in as many words, who can read
// the note - before the button is pressed, not after. Nothing here says "only
// people you send it to", because that is not what a link is.
//
// AT THE FOOT OF THE EDITOR, under the note it is about. Depth is reachable and
// never surfaced (AGENTS.md): somebody writing a shopping list should not be
// offered a way to publish it before they can type, and somebody who wants to
// share finds the control under the thing they are sharing, with the sentence
// that explains it. It is the same shape "New section" takes at the foot of the
// notes list.
//
// ONLY A NOTE OF ITS OWN. An `item` note lives in `routine.note` and there is
// deliberately no way to share one - the share row references `note`, so this
// component is simply not mounted for an item (see `NoteEditor`). That is the
// same boundary pinning and grouping already keep, for the same reason: the
// Notes screen's vocabulary stays off the table the day and the board are read
// from.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isSignedOut, messageFor } from '../api/client.ts';
import type { Note, NoteShare } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';

/** How long "Copied" stands before the button says what it does again. */
const COPIED_MS = 2000;

type ShareReader = { current: boolean };

interface ShareWork {
  readers: Set<ShareReader>;
  write: Promise<void> | null;
}

const workByNote = new Map<string, ShareWork>();

function workFor(noteId: string): ShareWork {
  const existing = workByNote.get(noteId);
  if (existing !== undefined) return existing;
  const created: ShareWork = { readers: new Set(), write: null };
  workByNote.set(noteId, created);
  return created;
}

function releaseWork(noteId: string, work: ShareWork): void {
  if (work.write === null && work.readers.size === 0 && workByNote.get(noteId) === work) {
    workByNote.delete(noteId);
  }
}

async function readCurrentShare(
  noteId: string,
  live: () => boolean,
): Promise<NoteShare | null | undefined> {
  while (live()) {
    const work = workFor(noteId);
    const pending = work.write;
    if (pending !== null) {
      await pending;
      continue;
    }

    const reader: ShareReader = { current: true };
    work.readers.add(reader);
    try {
      const share = await api.noteShare(noteId);
      if (!live()) return undefined;
      if (reader.current) return share;
    } catch (error) {
      if (!live()) return undefined;
      if (reader.current) throw error;
    } finally {
      work.readers.delete(reader);
      releaseWork(noteId, work);
    }
  }
  return undefined;
}

function writeShare<T>(noteId: string, operation: () => Promise<T>): Promise<T> {
  const work = workFor(noteId);
  for (const reader of work.readers) reader.current = false;
  const previous = work.write;
  const result = (async () => {
    if (previous !== null) await previous;
    return operation();
  })();
  const settled = result.then(
    () => undefined,
    () => undefined,
  );
  work.write = settled;
  void settled.then(() => {
    if (work.write === settled) work.write = null;
    releaseWork(noteId, work);
  });
  return result;
}

interface Props {
  /** The note being edited. Always `kind: 'note'` - see the header. */
  note: Note;
  shared?: boolean;
  /**
   * A link was made or destroyed, so the list behind this screen is now wrong.
   *
   * The Notes list draws a badge on every published note, and that badge is a
   * safety property rather than decoration - so the write that changes it has to
   * say so. Without this, sharing a note and pressing Back leaves the list
   * showing it as private until the page is reloaded, which is exactly the
   * "a write that tells nothing" defect this client has already shipped once
   * (AGENTS.md, the board's `refresh()`).
   */
  onChanged: (noteId: string, shared: boolean) => void;
  onSignedOut: () => void;
  say: (message: string) => void;
  saveBeforeSharing: () => Promise<boolean>;
}

export function ShareNote({ note, shared, onChanged, onSignedOut, say, saveBeforeSharing }: Props) {
  const isShared = shared ?? note.shared;
  // `undefined` is "not asked yet", which is a different thing from `null`,
  // "asked, and this note has no link". The list payload says whether there IS
  // one (`note.shared`), so an unshared note costs no request at all - only a
  // note that is already published has a token to go and fetch.
  const [share, setShare] = useState<NoteShare | null | undefined>(isShared ? undefined : null);
  const [confirmedMissing, setConfirmedMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [readProblem, setReadProblem] = useState<string | null>(null);
  const [writeProblem, setWriteProblem] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  // THE READ FAILED, held apart from the messages on purpose. A write clears
  // the read's message while it runs, so a state drawn from that message alone
  // would take its own buttons away the moment Stop sharing was pressed - which
  // is the control disappearing exactly when it is being used.
  const [readFailed, setReadFailed] = useState(false);
  // Bumped by Try again, and in the read effect's dependencies: without it the
  // effect has nothing that changes after a failure and would never ask twice.
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const failed = useCallback(
    (error: unknown, what: string): void => {
      if (isSignedOut(error)) {
        onSignedOut();
        return;
      }
      setReadProblem(`${what} ${messageFor(error)}`);
    },
    [onSignedOut],
  );

  const writeFailed = useCallback(
    (error: unknown, what: string): void => {
      if (isSignedOut(error)) {
        onSignedOut();
        return;
      }
      const message = `${what} ${messageFor(error)}`;
      if (mounted.current) setWriteProblem(message);
      else say(message);
    },
    [onSignedOut, say],
  );

  useEffect(() => {
    if (isShared) return;
    setShare(null);
    setConfirmedMissing(false);
    setReadFailed(false);
    setReadProblem(null);
    setCopied(false);
  }, [isShared]);

  // The link this note already has. Only ever runs for a note the list said was
  // shared, and it is a read of the OWNER's own data through the ordinary
  // session - the token is not secret from the person who published it.
  useEffect(() => {
    if (!isShared) return;
    let live = true;
    void (async () => {
      try {
        const found = await readCurrentShare(note.id, () => live);
        if (found === undefined) return;
        setShare(found);
        setConfirmedMissing(found === null);
        setReadFailed(false);
        setReadProblem(null);
        if (found === null) onChanged(note.id, false);
      } catch (error) {
        if (!live) return;
        // `share` is left as `undefined`, which draws Try again and STOP
        // SHARING - never "Create a link". A note the list says is shared, whose
        // link could not be read, must not be drawn as one that is not shared:
        // that would offer to create a link for a note that already has one, and
        // the answer would be the existing link arriving as a surprise.
        setReadFailed(true);
        failed(error, 'The link for this note could not be read.');
      }
    })();
    return () => {
      live = false;
    };
  }, [note.id, isShared, attempt, failed, onChanged]);

  const currentShare = confirmedMissing
    ? null
    : shared === false
      ? null
      : shared === true && share === null
        ? undefined
        : share;

  const create = async (): Promise<void> => {
    setBusy(true);
    setReadProblem(null);
    setWriteProblem(null);
    try {
      if (!(await saveBeforeSharing())) return;
      const created = await writeShare(note.id, () => api.shareNote(note.id));
      if (mounted.current) {
        setShare(created);
        setConfirmedMissing(false);
      }
      onChanged(note.id, true);
    } catch (error) {
      writeFailed(error, 'That note was not shared.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  // REACHABLE WITHOUT HAVING READ THE LINK, which is the whole of why it takes
  // the note's id and never the token. A safety control that disappears when a
  // read fails is not a control: the person whose note is public wants it to
  // stop being public, and whether this screen managed to fetch the URL is
  // nothing to do with that.
  const revoke = async (): Promise<void> => {
    setBusy(true);
    setReadProblem(null);
    setWriteProblem(null);
    try {
      await writeShare(note.id, () => api.revokeNoteShare(note.id));
      if (mounted.current) {
        setShare(null);
        setConfirmedMissing(true);
        setReadFailed(false);
        setCopied(false);
      }
      onChanged(note.id, false);
    } catch (error) {
      writeFailed(error, 'The link was not turned off.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  /** Ask for the link again. The one failure here that trying again can fix. */
  const reread = (): void => {
    setReadProblem(null);
    setAttempt((n) => n + 1);
  };

  const copy = (): void => {
    // SELECTED FIRST, AND ALWAYS. The clipboard API needs a secure context and
    // a permission that can be refused, so it is the thing that may fail - and
    // when it does, a link already selected in a field is one keystroke from
    // being copied by hand. Doing it the other way round leaves somebody with a
    // button that did nothing.
    field.current?.select();
    void navigator.clipboard?.writeText(currentShare?.url ?? '').then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <section className="sharebox">
      <h2>
        <Icon name="link" />
        Share a read-only link
      </h2>

      {currentShare === undefined ? (
        // The list said this note is published and the link has not arrived. It
        // is deliberately NOT drawn as "not shared": offering to create a link
        // for a note that already has one would hand back the existing one and
        // look like a bug.
        <>
          <p className="sharewait" role="status">
            {readFailed ? 'This note is shared.' : 'Loading the link…'}
          </p>
          {readFailed && (
            <>
              {/* BOTH, and Stop sharing is the one that matters. The link is
                  live whether or not this screen could read it, so the control
                  that makes it stop working may not depend on a successful
                  read - it needs the note's id and nothing else. Try again sits
                  beside it because a read that failed once is exactly the kind
                  that succeeds on the second ask. */}
              <div className="sharerow">
                <button className="btn" disabled={busy} onClick={reread}>
                  Try again
                </button>
                <button className="btn danger" disabled={busy} onClick={() => void revoke()}>
                  {busy ? 'Turning it off…' : 'Stop sharing'}
                </button>
              </div>
              <p className="finetext">
                The link itself could not be read, so it cannot be shown or copied here. It is still
                working, and turning it off still stops it working straight away, for everybody who
                has it.
              </p>
            </>
          )}
        </>
      ) : currentShare === null ? (
        <>
          <p className="finetext">
            Anyone with the link can read this note. They will not need an account, and they will
            see whatever the note says at the time they open it. Nothing else of yours is shared,
            and the link does not say who wrote it.
          </p>
          <button className="btn" disabled={busy} onClick={() => void create()}>
            {busy ? 'Making a link…' : 'Create a link'}
          </button>
        </>
      ) : (
        <>
          {/* Said again here rather than only on the button that made it: this
              is the state somebody comes back to a week later, and "who can read
              this" is the question they have then, not the one they had when
              they pressed it. */}
          <p className="sharesaid" role="status">
            This note is shared. Anyone with the link can read it, without an account.
          </p>
          <div className="sharelink">
            <input
              ref={field}
              type="text"
              readOnly
              value={currentShare.url}
              aria-label="Link to this note"
              onFocus={(event) => event.currentTarget.select()}
            />
            <button className="btn" onClick={copy}>
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
          {/* One press and no confirmation, and no Undo on a toast either -
              which is this client's one exception to "undo on every destructive
              action", stated where it is felt. What is destroyed is a link, not
              writing: the note is untouched, and an undo that brought the same
              URL back would be the opposite of what this button is for. The
              consequence is said BEFORE the press instead. */}
          <button className="btn danger" disabled={busy} onClick={() => void revoke()}>
            {busy ? 'Turning it off…' : 'Stop sharing'}
          </button>
          <p className="finetext">
            Turning it off stops the link working straight away, for everybody who has it. Sharing
            this note again makes a different link - the old one never works again. Anyone who has
            already read it may still have their own copy.
          </p>
        </>
      )}

      {(writeProblem ?? readProblem) !== null && (
        <p className="sheetproblem" role="alert">
          {writeProblem ?? readProblem}
        </p>
      )}
    </section>
  );
}
